<script setup>
import { computed, ref, watch } from "vue";
import Service from "@/api/services/socialFundApplication.service";
import Notification from "@/utils/plugins/NotificationService";
import { useUiStore } from "@/app/stores/uiStore";
import { socialFundRichTextContent } from "@/utils/socialFundRichText";
import { apiErrorMessage } from "@/utils/socialFundV2";
const props = defineProps({
  modelValue: Boolean,
  recipients: { type: Array, default: () => [] },
});
const emit = defineEmits(["update:modelValue", "sent"]);
const uiStore = useUiStore();
const subject = ref("");
const message = ref("");
const messageEditorKey = ref(0);
const messageText = computed(() => socialFundRichTextContent(message.value));
const messageTooLong = computed(() => message.value.length > 5000);
const confirming = ref(false);
const sending = ref(false);
const error = ref("");
const visible = computed({
  get: () => props.modelValue,
  set: (value) => {
    if (!sending.value) emit("update:modelValue", value);
  },
});
const valid = computed(
  () =>
    !!subject.value.trim() &&
    subject.value.length <= 200 &&
    !!messageText.value &&
    !messageTooLong.value &&
    props.recipients.length > 0 &&
    new Set(props.recipients.map((item) => String(item.id))).size ===
      props.recipients.length,
);
const duplicateEmails = computed(() => {
  const emails = props.recipients
    .map((item) => item.email?.trim().toLowerCase())
    .filter(Boolean);
  return new Set(emails).size !== emails.length;
});
watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      subject.value = "";
      message.value = "";
      messageEditorKey.value += 1;
      confirming.value = false;
      error.value = "";
    }
  },
);
watch([subject, message, () => props.recipients], () => {
  confirming.value = false;
});
const send = async () => {
  if (!valid.value || !confirming.value || sending.value) return;
  sending.value = true;
  error.value = "";
  uiStore.startLoading();
  try {
    const response = await Service.sendCommunication({
      applicationIds: props.recipients.map((item) => item.id),
      subject: subject.value.trim(),
      message: message.value.trim(),
    });
    const count = response.data?.sentCount ?? props.recipients.length;
    if (count !== props.recipients.length || response.data?.failedCount > 0) {
      error.value =
        "El servidor informa de un envío parcial. Revisa los destinatarios antes de repetir el envío.";
      confirming.value = false;
      return;
    }
    Notification.success(
      "Comunicado enviado a " + count + " solicitantes.",
      5000,
    );
    emit("sent");
    emit("update:modelValue", false);
  } catch (err) {
    error.value = await apiErrorMessage(
      err,
      "No se ha confirmado el envío. Comprueba su estado antes de reintentarlo para evitar duplicados.",
    );
    confirming.value = false;
  } finally {
    sending.value = false;
    uiStore.stopLoading();
  }
};
</script>

<template>
  <el-dialog
    v-model="visible"
    title="Enviar comunicado a solicitantes"
    width="700px"
    class="social-fund-communication-dialog"
    :close-on-click-modal="!sending"
    :close-on-press-escape="!sending"
    :show-close="!sending"
  >
    <form @submit.prevent="confirming ? send() : (confirming = valid)">
      <p>
        El comunicado se enviará por email y aparecerá en el chat de cada
        expediente.
      </p>
      <label for="communication-subject">Asunto (obligatorio)</label>
      <el-input
        id="communication-subject"
        v-model="subject"
        class="w-100"
        maxlength="200"
        show-word-limit
        :disabled="sending"
      />
      <label id="communication-message-label">Mensaje (obligatorio)</label>
      <div class="social-fund-communication-dialog__editor">
        <QuillEditor :key="messageEditorKey" v-model:content="message" content-type="html" theme="snow"
          :read-only="sending" :toolbar="[['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'clean']]"
          @ready="editor => { editor.root.setAttribute('aria-labelledby', 'communication-message-label'); editor.root.setAttribute('role', 'textbox'); editor.root.setAttribute('aria-multiline', 'true'); }" />
      </div>
      <p class="text-end small mb-0" aria-live="polite">{{ messageText.length }} caracteres</p>
      <p v-if="messageTooLong" role="alert">El mensaje es demasiado largo. El máximo es de 5000 caracteres incluyendo el formato; reduce el texto o el formato.</p>
      <details>
        <summary>Revisar {{ recipients.length }} destinatarios</summary>
        <ul>
          <li v-for="item in recipients" :key="item.id">
            {{ item.reference }} · {{ item.applicant }} ·
            {{ item.email || "Sin email disponible en la respuesta" }}
          </li>
        </ul>
      </details>
      <p v-if="duplicateEmails" role="status">
        Hay emails repetidos. Se enviará un email por expediente.
      </p>
      <p v-if="error" role="alert">{{ error }}</p>
      <p v-if="confirming" role="alert">
        <strong
          >Se enviará a {{ recipients.length }} solicitantes.
          ¿Continuar?</strong
        >
      </p>
      <footer>
        <button
          type="button"
          class="btn btn_yellow"
          :disabled="sending"
          @click="confirming ? (confirming = false) : (visible = false)"
        >
          {{ confirming ? "VOLVER A EDITAR" : "CANCELAR" }}
        </button>
        <button type="submit" class="btn btn_red" :disabled="!valid || sending">
          {{
            sending
              ? "ENVIANDO..."
              : confirming
                ? "CONFIRMAR ENVÍO"
                : "REVISAR ENVÍO"
          }}
        </button>
      </footer>
    </form>
  </el-dialog>
</template>

<style scoped lang="scss">
:global(.el-dialog.social-fund-communication-dialog) {
  border-radius: 20px;
  max-width: calc(100vw - 24px);
}
.social-fund-communication-dialog label {
  display: block;
  font-weight: 800;
  margin: 14px 0 6px;
}
.social-fund-communication-dialog__editor { min-width: 0; }
.social-fund-communication-dialog__editor :deep(.ql-toolbar) { display: flex; flex-wrap: wrap; }
.social-fund-communication-dialog__editor :deep(.ql-editor) { min-height: 150px; max-height: 32dvh; overflow-wrap: anywhere; }
.social-fund-communication-dialog details {
  margin-top: 16px;
}
.social-fund-communication-dialog details ul { max-height: min(240px, 30dvh); overflow-y: auto; padding-left: 18px; }
.social-fund-communication-dialog li {
  overflow-wrap: anywhere;
  margin: 6px 0;
}
.social-fund-communication-dialog footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}
.social-fund-communication-dialog .btn_red {
  color: #fff;
}
@media (max-width: 767px) {
  .social-fund-communication-dialog footer {
    flex-direction: column-reverse;
  }
  :global(.el-dialog.social-fund-communication-dialog) {
    margin-top: 12px;
    margin-bottom: 12px;
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
  }
}
</style>
